/**
 * Eigener Tooltip für den Performance-Chart.
 * Zeigt den Tag (Datum), die Serien-Werte und welche Änderungen an diesem Tag passierten.
 */
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import type { ChangeEvent } from '@/lib/api/performance'
import type { Metric } from '@/lib/products/salesSeriesTransforms'

interface PayloadItem { name?: string; value?: number | null; dataKey?: string; color?: string }

interface Props {
  active?: boolean
  payload?: PayloadItem[]
  label?: string
  eventsByDay: Map<string, ChangeEvent[]>
  metric: Metric
}

/** i18n-Label eines Events (gleiche Logik wie PerformanceEventList). */
function eventTitle(ev: ChangeEvent, t: TFunction): string {
  return ev.event_type === 'image'
    ? t('performance.events.image', { slot: ev.slot ?? '?' })
    : t(`performance.blockType.${ev.block_type}`, { defaultValue: ev.block_type ?? '' })
}

export function PerformanceChartTooltip({ active, payload, label, eventsByDay, metric }: Props) {
  const { t, i18n } = useTranslation()
  if (!active || !label) return null

  const fmt = (v: number) => (metric === 'revenue' ? `${v.toFixed(2)} €` : String(Math.round(v)))
  const lang = i18n?.language || 'de'
  const dateLabel = new Date(`${label}T00:00:00Z`).toLocaleDateString(lang, {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC',
  })
  const dayEvents = eventsByDay.get(label) ?? []
  const series = (payload ?? []).filter((p) => p.value != null)

  return (
    <div className="rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-xs shadow-md">
      <p className="font-semibold text-neutral-900 dark:text-neutral-50 mb-1">{dateLabel}</p>
      {series.map((p) => (
        <p key={p.dataKey} className="text-neutral-600 dark:text-neutral-300">
          <span aria-hidden="true" style={{ color: p.color }}>■</span> {p.name}: {fmt(p.value as number)}
        </p>
      ))}
      {dayEvents.length > 0 && (
        <div className="mt-1.5 border-t border-neutral-100 dark:border-neutral-800 pt-1.5">
          <p className="font-medium text-amber-600 dark:text-amber-400 mb-0.5">{t('performance.eventList.title')}</p>
          <ul className="space-y-0.5">
            {dayEvents.map((ev) => (
              <li key={ev.id} className="text-neutral-700 dark:text-neutral-200">• {eventTitle(ev, t)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
